import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { ArrowForward } from '@mui/icons-material'
import { Dialog, DialogContent, Divider, Skeleton, Stack, Typography } from '@mui/material'

import GRDialogTitle from '../../../../../components/GRDialogTitle/GRDialogTitle'
import { useGetAnalysisNewsEntryQuery } from '../../../../api/game-analyses'
import { GameAnalysis } from '../../types/GameAnalyses'
import { AnalysisInfoSummary } from '../AnalysisInfoSummary/AnalysisInfoSummary'

type AnalysisInfoDialogProps = {
  open: boolean
  gameName: string
  analysis?: GameAnalysis
  positionName?: string
  onClose: () => void
}

export const AnalysisInfoDialog: FC<AnalysisInfoDialogProps> = ({ open, gameName, analysis, positionName, onClose }) => {
  const { t } = useTranslation()
  const entryQuery = useGetAnalysisNewsEntryQuery(analysis?.id ?? '', { skip: !open || !analysis })
  const features = entryQuery.currentData?.entryData?.features ?? []

  const renderFeatures = () => {
    if (entryQuery.isFetching) return <Skeleton variant="rectangular" height={48} />
    if (entryQuery.isError) return <Typography color="error.main">{t('internal-analysts:games_analyses_changed_features_failed')}</Typography>
    if (!features.length) return <Typography color="text.secondary">{t('internal-analysts:games_analyses_changed_features_none')}</Typography>
    return features.map((feature, index) => (
      <Stack direction="row" spacing={1} alignItems="center" key={`${feature.featureId ?? feature.featureName}-${index}`}>
        <Typography variant="body2" fontWeight="bold">
          {feature.featureName}:
        </Typography>
        <Typography variant="body2">{feature.choice2}</Typography>
        <ArrowForward fontSize="small" color="action" />
        <Typography variant="body2" color="success.main">
          {feature.choice1}
        </Typography>
      </Stack>
    ))
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <GRDialogTitle onClose={onClose}>{t('internal-analysts:games_analyses_changed_features_title', { name: gameName })}</GRDialogTitle>
      <DialogContent>
        {analysis && (
          <Stack spacing={2}>
            <Typography fontWeight="bold">
              {t('internal-analysts:games_analyses_changed_features_version', { version: analysis.gameVersion || '-' })}
            </Typography>
            <AnalysisInfoSummary analysis={analysis} positionName={positionName} />
            <Divider />
            <Typography variant="overline">{t('internal-analysts:games_analyses_changed_features')}</Typography>
            {renderFeatures()}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  )
}
